import {
  ChunkDelta,
  SaveGame,
  SaveMetadata,
  SaveRepository,
  SettingsRepository,
  WorldRepository,
} from './WorldRepository';

const DB_NAME = 'solar-frontier';
const DB_VERSION = 1;
const STORE_CHUNKS = 'chunkDeltas';
const STORE_SAVES = 'saves';
const STORE_SETTINGS = 'settings';

/**
 * IndexedDB-backed persistence for the local prototype.
 *
 * Only *deltas* are written. The base world is regenerated from seeds, so a save
 * is kilobytes rather than gigabytes, and a player who has explored half a
 * planet has a save proportional to what they changed, not to where they went.
 *
 * When the project moves to a server, `ApiWorldRepository` implements the same
 * interfaces and nothing above this layer is touched.
 */
export class IndexedDbRepository implements WorldRepository, SaveRepository {
  private dbPromise: Promise<IDBDatabase> | null = null;

  private open(): Promise<IDBDatabase> {
    if (this.dbPromise) return this.dbPromise;
    this.dbPromise = new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        reject(new Error('IndexedDB is not available in this environment'));
        return;
      }
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_CHUNKS)) {
          db.createObjectStore(STORE_CHUNKS);
        }
        if (!db.objectStoreNames.contains(STORE_SAVES)) {
          db.createObjectStore(STORE_SAVES, { keyPath: 'metadata.id' });
        }
        if (!db.objectStoreNames.contains(STORE_SETTINGS)) {
          db.createObjectStore(STORE_SETTINGS);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('Failed to open IndexedDB'));
    });
    return this.dbPromise;
  }

  private async run<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    const db = await this.open();
    return new Promise<T>((resolve, reject) => {
      const tx = db.transaction(store, mode);
      const req = fn(tx.objectStore(store));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error(`IndexedDB ${mode} on ${store} failed`));
    });
  }

  // ---- WorldRepository -----------------------------------------------------

  private chunkKey(planetId: string, chunkId: string): string {
    return `${planetId}/${chunkId}`;
  }

  async loadChunkDelta(planetId: string, chunkId: string): Promise<ChunkDelta | null> {
    try {
      const value = await this.run<ChunkDelta | undefined>(STORE_CHUNKS, 'readonly', (s) =>
        s.get(this.chunkKey(planetId, chunkId)),
      );
      return value ?? null;
    } catch {
      return null;
    }
  }

  async saveChunkDelta(planetId: string, chunkId: string, delta: ChunkDelta): Promise<void> {
    const empty =
      delta.terrainModifications.length === 0 &&
      delta.discoveredLocationIds.length === 0 &&
      delta.destroyedObjectIds.length === 0;

    // Never store an empty delta: the whole point is that unchanged world costs
    // nothing at all.
    if (empty) {
      await this.run(STORE_CHUNKS, 'readwrite', (s) => s.delete(this.chunkKey(planetId, chunkId)));
      return;
    }
    await this.run(STORE_CHUNKS, 'readwrite', (s) => s.put(delta, this.chunkKey(planetId, chunkId)));
  }

  async loadPlanetDeltas(planetId: string): Promise<Map<string, ChunkDelta>> {
    const result = new Map<string, ChunkDelta>();
    try {
      const db = await this.open();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_CHUNKS, 'readonly');
        const store = tx.objectStore(STORE_CHUNKS);
        const range = IDBKeyRange.bound(`${planetId}/`, `${planetId}/￿`);
        const cursorReq = store.openCursor(range);
        cursorReq.onsuccess = () => {
          const cursor = cursorReq.result;
          if (!cursor) {
            resolve();
            return;
          }
          const key = String(cursor.key);
          result.set(key.slice(planetId.length + 1), cursor.value as ChunkDelta);
          cursor.continue();
        };
        cursorReq.onerror = () => reject(cursorReq.error);
      });
    } catch {
      // A read failure should degrade to "no modifications", never to a crash.
    }
    return result;
  }

  async clearPlanet(planetId: string): Promise<void> {
    const db = await this.open();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_CHUNKS, 'readwrite');
      const store = tx.objectStore(STORE_CHUNKS);
      const range = IDBKeyRange.bound(`${planetId}/`, `${planetId}/￿`);
      const req = store.delete(range);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  // ---- SaveRepository ------------------------------------------------------

  async list(): Promise<SaveMetadata[]> {
    try {
      const all = await this.run<SaveGame[]>(STORE_SAVES, 'readonly', (s) => s.getAll());
      return all.map((s) => s.metadata).sort((a, b) => b.savedAt - a.savedAt);
    } catch {
      return [];
    }
  }

  async load(id: string): Promise<SaveGame | null> {
    try {
      const value = await this.run<SaveGame | undefined>(STORE_SAVES, 'readonly', (s) => s.get(id));
      return value ?? null;
    } catch {
      return null;
    }
  }

  async save(save: SaveGame): Promise<void> {
    await this.run(STORE_SAVES, 'readwrite', (s) => s.put(save));
  }

  async delete(id: string): Promise<void> {
    await this.run(STORE_SAVES, 'readwrite', (s) => s.delete(id));
  }

  // ---- SettingsRepository --------------------------------------------------

  async loadSettings(): Promise<Record<string, unknown>> {
    try {
      const value = await this.run<Record<string, unknown> | undefined>(STORE_SETTINGS, 'readonly', (s) =>
        s.get('user'),
      );
      return value ?? {};
    } catch {
      return {};
    }
  }

  async saveSettings(settings: Record<string, unknown>): Promise<void> {
    await this.run(STORE_SETTINGS, 'readwrite', (s) => s.put(settings, 'user'));
  }
}

/**
 * In-memory implementation.
 *
 * Used by tests and as an automatic fallback when IndexedDB is unavailable
 * (private browsing modes block it). The game runs identically; the session is
 * simply not persisted, and the player is told so rather than silently losing
 * their progress at the end.
 */
export class MemoryRepository implements WorldRepository, SaveRepository {
  private chunks = new Map<string, ChunkDelta>();
  private saves = new Map<string, SaveGame>();
  private settings: Record<string, unknown> = {};

  async loadChunkDelta(planetId: string, chunkId: string): Promise<ChunkDelta | null> {
    return this.chunks.get(`${planetId}/${chunkId}`) ?? null;
  }

  async saveChunkDelta(planetId: string, chunkId: string, delta: ChunkDelta): Promise<void> {
    this.chunks.set(`${planetId}/${chunkId}`, delta);
  }

  async loadPlanetDeltas(planetId: string): Promise<Map<string, ChunkDelta>> {
    const out = new Map<string, ChunkDelta>();
    for (const [key, value] of this.chunks) {
      if (key.startsWith(`${planetId}/`)) out.set(key.slice(planetId.length + 1), value);
    }
    return out;
  }

  async clearPlanet(planetId: string): Promise<void> {
    for (const key of Array.from(this.chunks.keys())) {
      if (key.startsWith(`${planetId}/`)) this.chunks.delete(key);
    }
  }

  async list(): Promise<SaveMetadata[]> {
    return Array.from(this.saves.values())
      .map((s) => s.metadata)
      .sort((a, b) => b.savedAt - a.savedAt);
  }

  async load(id: string): Promise<SaveGame | null> {
    return this.saves.get(id) ?? null;
  }

  async save(save: SaveGame): Promise<void> {
    this.saves.set(save.metadata.id, save);
  }

  async delete(id: string): Promise<void> {
    this.saves.delete(id);
  }

  async loadSettings(): Promise<Record<string, unknown>> {
    return this.settings;
  }

  async saveSettings(settings: Record<string, unknown>): Promise<void> {
    this.settings = settings;
  }
}

/** Picks the best available storage, falling back gracefully. */
export async function createRepository(): Promise<{
  world: WorldRepository;
  saves: SaveRepository;
  settings: SettingsRepository;
  persistent: boolean;
}> {
  if (typeof indexedDB !== 'undefined') {
    const repo = new IndexedDbRepository();
    try {
      // Probe with a real read; some browsers only fail on first use.
      await repo.list();
      const adapter: SettingsRepository = {
        load: () => repo.loadSettings(),
        save: (s) => repo.saveSettings(s),
      };
      return { world: repo, saves: repo, settings: adapter, persistent: true };
    } catch (err) {
      console.warn('[persistence] IndexedDB unavailable, falling back to memory storage', err);
    }
  }
  const memory = new MemoryRepository();
  const adapter: SettingsRepository = {
    load: () => memory.loadSettings(),
    save: (s) => memory.saveSettings(s),
  };
  return { world: memory, saves: memory, settings: adapter, persistent: false };
}
